/**
 * ACCOUNT SWITCHES, as both clients must handle them: the moment a different
 * person is signed in on this tab / this phone without the page reloading or
 * the app restarting, every per-user cache from before belongs to someone
 * else.
 *
 * Nothing else in the app keys its react-query cache by user. Social's `me`,
 * the people search, study groups, credits — they all live under keys with no
 * user in them, so after a switch they answer with the PREVIOUS account's rows
 * until a refetch lands. Measured in prod (Sentry FLASHKA-VITE-277): a web tab
 * opened the next account's database and, within a second, PATCHed that
 * account's social profile from the previous account's cached `/me` row — and
 * got a 404.
 *
 * The switches that reach a live client without a reload:
 *  - web: the sign-in dialog signs a visitor in in place; a sign-in in ANOTHER
 *    tab replaces the session cookie and this tab's session store picks the
 *    new user up on its next refetch (focus, poll). Web's own sign-out reloads.
 *  - mobile: sign-out never restarts the app, so the next sign-in inherits
 *    everything the last account cached.
 *
 * Fed from better-auth's `session` atom, which every session transition passes
 * through (each client's `auth/better/client.ts`). Pure and free of
 * better-auth / react-query types: the atom's value and the query client are
 * narrowed structurally.
 */

import { sessionCheck } from './session-check';

/**
 * Who the session atom says is signed in, once it actually KNOWS: the user id,
 * `null` for a definitive signed-out answer, `undefined` while it cannot say —
 * the boot fetch still pending, or a session check that failed without the
 * server answering (offline, a 5xx), which better-auth reports as `data: null`
 * just like a signed-out visitor.
 */
export function settledSessionUserId(value: unknown): string | null | undefined {
  const state = value as {
    data?: { user?: { id?: unknown } } | null;
    error?: unknown;
    isPending?: unknown;
  } | null;
  if (typeof state !== 'object' || state === null) return undefined;
  const id = state.data?.user?.id;
  if (typeof id === 'string') return id;
  if (state.isPending === true) return undefined;
  return sessionCheck({ hasUser: false, error: state.error }) === 'signed-out'
    ? null
    : undefined;
}

export type AccountSwitchListener = (userId: string) => void;

export interface AccountSwitchSignal {
  /** Feed every session-atom value through here. */
  observe: (sessionAtomValue: unknown) => void;
  /** Called with the new user's id each time a DIFFERENT account signs in. */
  subscribe: (listener: AccountSwitchListener) => () => void;
}

/**
 * Fires when a signed-in user other than the last settled one appears:
 * A → B, signed-out → B, and A → signed-out → B alike.
 *
 * Deliberately silent for:
 *  - the boot: the first settled value is the baseline, not a switch — nothing
 *    cached before it belongs to anyone else;
 *  - a switch TO signed-out: nothing per-user is shown signed out (web's
 *    sign-out reloads the page outright), and resetting then would refetch
 *    every mounted query unauthenticated. The next sign-in fires instead;
 *  - refetch churn and failed checks that keep the same user.
 */
export function createAccountSwitchSignal(): AccountSwitchSignal {
  const listeners = new Set<AccountSwitchListener>();
  // `undefined` until the session first settles.
  let last: string | null | undefined;
  return {
    observe(value) {
      const next = settledSessionUserId(value);
      if (next === undefined || next === last) return;
      const previous = last;
      last = next;
      if (previous === undefined || next === null) return;
      for (const listener of listeners) {
        // One broken subscriber must not starve the others, nor throw into the
        // atom subscription the JWT cache rides on.
        try {
          listener(next);
        } catch {
          /* best-effort */
        }
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** The slice of a TanStack `Query` the deferred refetch reads. */
interface ResettableQuery {
  state: { dataUpdateCount: number; errorUpdateCount: number; fetchStatus: string };
}

/** Still exactly as the reset left it: never answered, nothing in flight. */
function untouchedSinceReset({ state }: ResettableQuery): boolean {
  return (
    state.dataUpdateCount === 0 &&
    state.errorUpdateCount === 0 &&
    state.fetchStatus === 'idle'
  );
}

/** The slice of a TanStack `QueryClient` the reset needs. */
export interface ResettableQueryClient {
  getQueryCache: () => { findAll: () => ReadonlyArray<{ reset: () => void }> };
  refetchQueries: (
    filters: { type: 'active'; predicate: (query: ResettableQuery) => boolean },
    options: { cancelRefetch: false },
  ) => Promise<unknown>;
}

/** `placeholderData` that keeps the previous key's rows — within one account. */
export type AccountScopedKeepPreviousData = <T>(
  previousData: T | undefined,
) => T | undefined;

const keepPreviousDataFn = (): AccountScopedKeepPreviousData => (previousData) =>
  previousData;
let keepWithinAccount = keepPreviousDataFn();

/**
 * Use in place of TanStack's `keepPreviousData`, read on every render:
 * `placeholderData: accountScopedKeepPreviousData()`.
 *
 * An observer already SHOWING a placeholder hands its own `data` straight back
 * after the reset whenever `placeholderData` is the same function as before —
 * TanStack's shortcut for a placeholder chain — so with `keepPreviousData` the
 * people search went on showing the previous account's rows as the next one's
 * placeholder (and kept them while the new key was disabled). Each account
 * switch mints a new function, which breaks that chain; between switches it is
 * stable, so the placeholder behaves exactly like `keepPreviousData`.
 */
export function accountScopedKeepPreviousData(): AccountScopedKeepPreviousData {
  return keepWithinAccount;
}

/**
 * Drop EVERY cached query for the account that just signed in — the same thing
 * a page reload gives web's sign-out — and refetch the ones on screen.
 *
 * Each query is RESET in place, never removed (`removeQueries` / `clear`): a
 * removed query object lives on inside its mounted observers, and a
 * keep-previous placeholder reads its data back for the next key — the previous
 * account's rows again. The reset also cancels the previous account's
 * in-flight fetches, so their answers never land.
 *
 * The reset is synchronous (it runs inside the session-atom notification, so
 * React never renders the new user over the old rows); the refetch waits a
 * macrotask. Run at once it would call each query's `queryFn` closure from the
 * PREVIOUS account's last render — a query keyed by user id refetches that
 * user's resource with the new account's token. A tick later the observers
 * have re-rendered, and those queries are no longer the active ones. Only
 * queries still untouched since the reset are asked: one that a key change on
 * that render already fetched for the new account is not fetched twice.
 */
export function resetQueryCacheForAccountSwitch(
  queryClient: ResettableQueryClient,
): void {
  keepWithinAccount = keepPreviousDataFn();
  for (const query of queryClient.getQueryCache().findAll()) query.reset();
  setTimeout(() => {
    void queryClient
      .refetchQueries(
        { type: 'active', predicate: untouchedSinceReset },
        { cancelRefetch: false },
      )
      .catch(() => undefined);
  }, 0);
}
